import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Building2, Camera, ChevronRight, CircleHelp, ClipboardList, FileText, LayoutDashboard, LayoutGrid, Paperclip, Save, SendHorizontal, Settings2, ShieldCheck, UserCog, UserPlus, Wrench, X, Zap } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { buildAppPath } from '@/utils/tenant';
import { canAccessSection, SECTIONS } from '@/utils/accessControl';
import { getRoleMetadata, normalizeRole, ROLES } from '@/utils/roles';
import tokenManager from '@/services/auth/tokenManager';
import { setUser } from 'redux/slices/authSlice';
import { useUploadUserProfilePhotoMutation } from 'redux/services/admin/userManagementV2';
import { useCreateSupportTicketMutation } from 'redux/services/support';
import RolesAccessPanel from './RolesAccessPanel';
import styles from './AccountQuickPanel.module.css';

const toTitleCase = (value = '') =>
  String(value)
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

const getInitials = (name = '') =>
  String(name)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'U';

const getUserId = (user = {}) =>
  user?.user_id || user?.userId || user?.id || null;

const getPhotoUrlFromResponse = (response = {}) =>
  response?.data?.profile_picture_url ||
  response?.profile_picture_url ||
  response?.data?.user?.profile_picture_url ||
  response?.user?.profile_picture_url ||
  response?.data?.profilePictureUrl ||
  response?.profilePictureUrl ||
  '';

const normalizeErrorText = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value.map(normalizeErrorText).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    const directMessage =
      value?.detail || value?.message || value?.error || value?.title || value?.reason;
    if (directMessage) return normalizeErrorText(directMessage);

    const fieldMessages = Object.entries(value)
      .map(([field, fieldValue]) => {
        const text = normalizeErrorText(fieldValue);
        return text ? `${field}: ${text}` : '';
      })
      .filter(Boolean);

    if (fieldMessages.length) return fieldMessages.join(' | ');
  }

  return '';
};

const getReadableErrorMessage = (error, fallback) => {
  const candidates = [
    error?.data?.detail,
    error?.data?.message,
    error?.data?.error,
    error?.error,
    error?.message,
    error?.data,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeErrorText(candidate);
    if (normalized) return normalized;
  }

  return fallback;
};

const normalizeApplicationId = (value) => {
  if (!value) return '';
  const normalized = String(value).trim();
  if (!normalized || normalized === 'null' || normalized === 'undefined') {
    return '';
  }
  return normalized;
};

// Verbatim from the design reference's Topic select. Free text would be
// kinder to the sender and worse for whoever triages the queue, so the list
// stays closed.
const SUPPORT_TOPICS = [
  'General question',
  "Something's broken",
  'Feature request',
  'Access / permissions',
  'Billing',
];
const SUPPORT_EMAIL = 'support@miralabs.ai';


const PRIMARY_B2B_ACTION_KEYS = ['dashboard', 'loan-info', 'pipeline', 'quick-price', 'tools'];
const ADMIN_B2B_ACTION_KEYS = ['reports', 'employees', 'consumers', 'branches', 'invitations'];

const WORK_AREA_GROUPS = [
  {
    key: 'primary',
    title: 'Primary Work',
    description: 'The areas you are most likely to use during day-to-day operations.',
  },
  {
    key: 'admin',
    title: 'Administration',
    description: 'Management tools for employees, consumers, branches, reporting, and invites.',
  },
];

const getB2BActionItems = ({ normalizedRole, navigate }) => {
  const actions = [];
  const pushAction = (key, label, icon, description, onClick, visible = true) => {
    if (!visible) return;
    actions.push({ key, label, icon, description, onClick });
  };

  pushAction('dashboard', 'Dashboard', LayoutDashboard, 'Overview and current activity.', () => navigate(buildAppPath('/dashboard')));
  pushAction(
    'loan-info',
    'Loan Information',
    FileText,
    'Open borrower file details.',
    () => navigate(buildAppPath('/loanInformation')),
    canAccessSection(SECTIONS.LOAN_INFORMATION, normalizedRole)
  );
  pushAction(
    'pipeline',
    'Pipeline',
    ClipboardList,
    'Work active files and statuses.',
    () => navigate(buildAppPath('/pipeline')),
    canAccessSection(SECTIONS.PIPELINE, normalizedRole)
  );
  pushAction(
    'quick-price',
    'Quick Price',
    Zap,
    'Run scenario pricing.',
    () => navigate(buildAppPath('/quickPrice')),
    [ROLES.CLIENT_ADMIN, ROLES.BRANCH_ADMIN, ROLES.LOAN_OFFICER].includes(normalizedRole)
  );
  pushAction(
    'tools',
    'Tools',
    Wrench,
    'Compare offers and schedules.',
    () => navigate(buildAppPath('/tools')),
    [ROLES.CLIENT_ADMIN, ROLES.BRANCH_ADMIN, ROLES.LOAN_OFFICER, ROLES.LOAN_PROCESSOR].includes(normalizedRole)
  );
  pushAction(
    'reports',
    'Reports',
    BarChart3,
    'Review tenant performance.',
    () => navigate(buildAppPath('/reports')),
    [ROLES.CLIENT_ADMIN, ROLES.BRANCH_ADMIN].includes(normalizedRole)
  );
  pushAction(
    'employees',
    'Manage Employees',
    UserCog,
    'Roles, access, and branch assignment.',
    () => navigate(buildAppPath('/admin/employees')),
    canAccessSection(SECTIONS.ADMIN, normalizedRole)
  );
  pushAction(
    'branches',
    'Manage Branches',
    Building2,
    'Review branch structure and settings.',
    () => navigate(buildAppPath('/admin/branches')),
    canAccessSection(SECTIONS.ADMIN, normalizedRole)
  );
  pushAction(
    'invitations',
    'Invitations',
    UserPlus,
    'Manage access invites.',
    () => navigate(buildAppPath('/admin/invitations')),
    canAccessSection(SECTIONS.ADMIN, normalizedRole)
  );
  return actions;
};

/* The loan the support form offers to reference. Tenant and branch went with
   the Workspace card that displayed them. */
const getActiveApplicationId = () => {
  if (typeof window === 'undefined') return '';

  const params = new URLSearchParams(window.location.search || '');
  return (
    params.get('applicationId') ||
    params.get('application_id') ||
    sessionStorage.getItem('b2c_active_application_id') ||
    localStorage.getItem('b2c_active_application_id') ||
    ''
  );
};

const AccountQuickPanel = ({
  user,
  mode = 'desktop',
  surface = 'popover',
  onClose,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const [localImageUrl, setLocalImageUrl] = useState('');
  // One section shows at a time, as the reference's tabs do. Profile is the
  // default because the reference opens on it.
  const [activeSection, setActiveSection] = useState('profile');
  const [quickForm, setQuickForm] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
  });
  const supportFileInputRef = useRef(null);
  const [supportFileName, setSupportFileName] = useState('');
  const [supportForm, setSupportForm] = useState({
    topic: SUPPORT_TOPICS[0],
    relatedLoan: '',
    subject: '',
    message: '',
  });

  useEffect(() => {
    return () => {
      if (localImageUrl) {
        URL.revokeObjectURL(localImageUrl);
      }
    };
  }, [localImageUrl]);

  const displayName = useMemo(() => {
    const fromNames = [user?.first_name, user?.last_name].filter(Boolean).join(' ');
    return user?.name || fromNames || user?.email || '';
  }, [user]);

  const normalizedRole = normalizeRole(user?.originalRole || user?.role || '');
  const roleMetadata = getRoleMetadata(normalizedRole);
  const role = roleMetadata?.label || toTitleCase(user?.originalRole || user?.role || '');

  const b2bActionItems = useMemo(
    () => getB2BActionItems({ normalizedRole, navigate }),
    [normalizedRole, navigate]
  );
  const b2bPrimaryItems = useMemo(
    () => PRIMARY_B2B_ACTION_KEYS.map((k) => b2bActionItems.find((i) => i.key === k)).filter(Boolean),
    [b2bActionItems]
  );
  const b2bAdminItems = useMemo(
    () => ADMIN_B2B_ACTION_KEYS.map((k) => b2bActionItems.find((i) => i.key === k)).filter(Boolean),
    [b2bActionItems]
  );

  const isDesktopB2BModal = mode !== 'mobile' && surface === 'modal';
  const applicationId = getActiveApplicationId();
  const normalizedApplicationId = normalizeApplicationId(applicationId);
  const [uploadUserProfilePhoto, { isLoading: isUploadingUserPhoto }] =
    useUploadUserProfilePhotoMutation();
  const [createSupportTicket, { isLoading: isSendingSupportTicket }] =
    useCreateSupportTicketMutation();
  const avatarUrl =
    localImageUrl ||
    user?.avatar_url ||
    user?.profile_picture ||
    user?.profile_picture_url ||
    '';
  const seedFirstName = user?.first_name || user?.firstName || '';
  const seedLastName = user?.last_name || user?.lastName || '';
  const seedPhoneNumber = user?.phone_number || user?.phoneNumber || user?.phone || '';
  const quickFormSeed = useMemo(() => ({
    firstName: seedFirstName,
    lastName: seedLastName,
    phoneNumber: seedPhoneNumber,
  }), [seedFirstName, seedLastName, seedPhoneNumber]);

  useEffect(() => {
    setQuickForm((prev) => {
      if (
        prev.firstName === seedFirstName &&
        prev.lastName === seedLastName &&
        prev.phoneNumber === seedPhoneNumber
      ) {
        return prev;
      }

      return {
        firstName: seedFirstName,
        lastName: seedLastName,
        phoneNumber: seedPhoneNumber,
      };
    });
  }, [
    normalizedApplicationId,
    seedFirstName,
    seedLastName,
    seedPhoneNumber,
  ]);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    void (async () => {
      const file = event.target.files?.[0];
      if (!file || !file.type.startsWith('image/')) return;

      const previousLocalImageUrl = localImageUrl;
      const previewUrl = URL.createObjectURL(file);
      setLocalImageUrl(previewUrl);

      try {
        const userId = getUserId(user);
        if (!userId) {
          throw new Error('Missing user id for profile photo upload');
        }
        const uploadResponse = await uploadUserProfilePhoto({ userId, file }).unwrap();

        const uploadedPhotoUrl = getPhotoUrlFromResponse(uploadResponse);
        if (!uploadedPhotoUrl) {
          toast({
            title: 'Photo uploaded, but URL was not returned.',
            description: 'Please refresh to view the latest profile photo.',
          });
          return;
        }

        const updatedUser = {
          ...user,
          profile_picture_url: uploadedPhotoUrl,
          profilePictureUrl: uploadedPhotoUrl,
          profile_picture: uploadedPhotoUrl,
          avatar_url: uploadedPhotoUrl,
        };

        dispatch(setUser(updatedUser));
        tokenManager.setUserData(updatedUser);

        if (previousLocalImageUrl) {
          URL.revokeObjectURL(previousLocalImageUrl);
        }
        setLocalImageUrl('');
        toast({ title: 'Profile photo updated.' });
      } catch (error) {
        if (previousLocalImageUrl) {
          URL.revokeObjectURL(previousLocalImageUrl);
        }
        URL.revokeObjectURL(previewUrl);
        setLocalImageUrl('');

        const message = getReadableErrorMessage(error, 'Unable to update profile photo.');
        toast({
          title: 'Photo upload failed',
          description: message,
          variant: 'destructive',
        });
      } finally {
        if (event?.target) {
          event.target.value = '';
        }
      }
    })();
  };

  const handleFieldChange = (field) => (event) => {
    let nextValue = event.target.value || '';
    if (field === 'phoneNumber') {
      nextValue = nextValue.replace(/[^\d+()\-\s]/g, '');
    }
    setQuickForm((prev) => ({ ...prev, [field]: nextValue }));
  };

  const handleQuickSave = () => {
    const nextFirstName = quickForm.firstName.trim();
    const nextLastName = quickForm.lastName.trim();
    const nextPhoneNumber = quickForm.phoneNumber.trim();

    if (!nextFirstName || !nextLastName) {
      toast({
        title: 'First and last name are required.',
        variant: 'destructive',
      });
      return;
    }

    const updatedUser = {
      ...user,
      first_name: nextFirstName,
      firstName: nextFirstName,
      last_name: nextLastName,
      lastName: nextLastName,
      phone_number: nextPhoneNumber,
      phoneNumber: nextPhoneNumber,
      phone: nextPhoneNumber,
      name: `${nextFirstName} ${nextLastName}`.trim(),
    };

    dispatch(setUser(updatedUser));
    tokenManager.setUserData(updatedUser);
    setActiveSection('profile');
    toast({ title: 'Account details updated.' });
  };

  const handleQuickCancel = () => {
    setQuickForm(quickFormSeed);
    setActiveSection('profile');
  };

  const handleSupportFieldChange = (field) => (event) => {
    const { value } = event.target;
    setSupportForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSupportFileChange = (event) => {
    const file = event.target.files?.[0];
    setSupportFileName(file?.name || '');
  };

  // Opening the form from inside a borrower file pre-fills the loan it is
  // about, matching the reference. Only seeds an empty field, so a sender who
  // has typed their own reference keeps it.
  useEffect(() => {
    if (activeSection !== 'support' || !normalizedApplicationId) return;
    setSupportForm((prev) =>
      prev.relatedLoan ? prev : { ...prev, relatedLoan: normalizedApplicationId }
    );
  }, [activeSection, normalizedApplicationId]);

  // Same endpoint and field names the standalone page posts, so a ticket filed
  // from here is indistinguishable from one filed there. The reference's
  // "Related loan" has no column of its own, so it rides on the description
  // rather than being dropped.
  const handleSupportSend = async () => {
    const description = [
      supportForm.relatedLoan ? `Related loan: ${supportForm.relatedLoan}` : '',
      supportForm.message.trim(),
    ]
      .filter(Boolean)
      .join('\n\n');

    const formData = new FormData();
    formData.append('email', user?.email || '');
    formData.append('contact', quickForm.phoneNumber || '');
    formData.append('subject', supportForm.subject.trim());
    formData.append('issue_type', supportForm.topic);
    formData.append('description', description);
    const file = supportFileInputRef.current?.files?.[0];
    if (file) formData.append('attachments', file);

    try {
      await createSupportTicket({ data: formData }).unwrap();
      setSupportForm({
        topic: SUPPORT_TOPICS[0],
        relatedLoan: '',
        subject: '',
        message: '',
      });
      setSupportFileName('');
      setActiveSection('profile');
      toast({ title: 'Ticket has been created successfully.' });
    } catch (error) {
      toast({
        title: 'Failed to create ticket',
        description: getReadableErrorMessage(error, 'Unable to submit ticket right now.'),
        variant: 'destructive',
      });
    }
  };

  return (
    <div
      className={`${styles.panel} ${mode === 'mobile' ? styles.mobile : ''} ${
        isDesktopB2BModal ? styles.panelModal : ''
      }`}
    >
      <div className={styles.headerRow}>
        <div>
          <p className={styles.headerCopy}>Account</p>
          {!isDesktopB2BModal ? (
            <p className={styles.headerDescription}>
              Review your workspace context, shortcuts, and basic account details.
            </p>
          ) : null}
        </div>
        {onClose ? (
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close account panel">
            <X size={14} />
          </button>
        ) : null}
      </div>

      {/* The identity block used to sit here, full width, above the whole
          layout — visible no matter which section you were in. The design
          reference puts it at the top of the account section instead, so it
          now renders inside My Profile beside the fields it describes. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

        <>
          <div className={isDesktopB2BModal ? styles.b2bModalLayout : ''}>
            <div className={isDesktopB2BModal ? styles.b2bSidebar : ''}>
              <div className={styles.section}>
                <p className={styles.sectionLabel}>Quick Actions</p>
                <div className={styles.actionList}>
                  <button
                    type="button"
                    className={styles.actionDetailBtn}
                    onClick={() => setActiveSection('profile')}
                    aria-current={activeSection === 'profile'}
                  >
                    <span className={styles.actionLeft}>
                      <span className={styles.actionGridIconWrap}>
                        <Settings2 className={styles.actionGridIcon} />
                      </span>
                      <span className={styles.actionDetailBody}>
                        <span className={styles.actionGridTitle}>My Profile</span>
                        <span className={styles.actionGridDescription}>Update your basic account details.</span>
                      </span>
                    </span>
                    <ChevronRight className={styles.actionArrow} />
                  </button>

                  <button
                    type="button"
                    className={styles.actionDetailBtn}
                    onClick={() => setActiveSection('work')}
                    aria-current={activeSection === 'work'}
                  >
                    <span className={styles.actionLeft}>
                      <span className={styles.actionGridIconWrap}>
                        <LayoutGrid className={styles.actionGridIcon} />
                      </span>
                      <span className={styles.actionDetailBody}>
                        <span className={styles.actionGridTitle}>Work Areas</span>
                        <span className={styles.actionGridDescription}>Jump to another part of the platform.</span>
                      </span>
                    </span>
                    <ChevronRight className={styles.actionArrow} />
                  </button>

                  {/* Admin only, matching the ADMIN tag the mock-up puts on this
                      section. Hidden rather than disabled for everyone else: a
                      disabled row still advertises that a permissions screen
                      exists. The server is what actually refuses the write. */}
                  {canAccessSection(SECTIONS.ADMIN, normalizedRole) ? (
                    <button
                      type="button"
                      className={styles.actionDetailBtn}
                      onClick={() => setActiveSection('roles')}
                      aria-current={activeSection === 'roles'}
                    >
                      <span className={styles.actionLeft}>
                        <span className={styles.actionGridIconWrap}>
                          <ShieldCheck className={styles.actionGridIcon} />
                        </span>
                        <span className={styles.actionDetailBody}>
                          <span className={styles.actionGridTitle}>Roles &amp; Access</span>
                          <span className={styles.actionGridDescription}>
                            Set what each role can do in a borrower file.
                          </span>
                        </span>
                      </span>
                      <ChevronRight className={styles.actionArrow} />
                    </button>
                  ) : null}

                  {/* Opens in place rather than navigating to /help-support.
                      The reference carries the support form as a section of
                      this panel, so leaving the account context to file a
                      ticket is no longer part of the flow. */}
                  <button
                    type="button"
                    className={styles.actionDetailBtn}
                    onClick={() => setActiveSection('support')}
                    aria-current={activeSection === 'support'}
                  >
                    <span className={styles.actionLeft}>
                      <span className={styles.actionGridIconWrap}>
                        <CircleHelp className={styles.actionGridIcon} />
                      </span>
                      <span className={styles.actionDetailBody}>
                        <span className={styles.actionGridTitle}>Help &amp; Support</span>
                        <span className={styles.actionGridDescription}>
                          Tell us what you need — we usually reply within 1 business day.
                        </span>
                      </span>
                    </span>
                    <ChevronRight className={styles.actionArrow} />
                  </button>
                </div>
              </div>

            </div>

            <div className={isDesktopB2BModal ? styles.b2bMain : ''}>
              {activeSection === 'profile' ? (
                <div className={styles.section}>
                  <div className={styles.quickEditCard}>
                    <div className={styles.profileHead}>
                      <span className={styles.profileHeadAvatar}>
                        {avatarUrl ? (
                          <img src={avatarUrl} alt="" className={styles.avatarImage} />
                        ) : (
                          getInitials(displayName)
                        )}
                      </span>
                      <div className={styles.profileHeadCopy}>
                        {displayName ? <p className={styles.name}>{displayName}</p> : null}
                        <p className={styles.email}>
                          {[roleMetadata?.label || role, user?.email].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                      <button
                        type="button"
                        className={styles.avatarAction}
                        onClick={handleUploadClick}
                        disabled={isUploadingUserPhoto}
                      >
                        <Camera size={13} />
                        {isUploadingUserPhoto ? 'Uploading...' : 'Change Photo'}
                      </button>
                    </div>
                    <div className={styles.quickEditGrid}>
                      <label className={styles.quickField}>
                        <span>First Name</span>
                        <input
                          type="text"
                          value={quickForm.firstName}
                          onChange={handleFieldChange('firstName')}
                          maxLength={60}
                        />
                      </label>
                      <label className={styles.quickField}>
                        <span>Last Name</span>
                        <input
                          type="text"
                          value={quickForm.lastName}
                          onChange={handleFieldChange('lastName')}
                          maxLength={60}
                        />
                      </label>
                      <label className={styles.quickField}>
                        <span>Phone Number</span>
                        <input
                          type="text"
                          value={quickForm.phoneNumber}
                          onChange={handleFieldChange('phoneNumber')}
                          maxLength={20}
                        />
                      </label>
                      <label className={styles.quickField}>
                        <span>Email</span>
                        <input
                          type="text"
                          value={user?.email || ''}
                          readOnly
                          disabled
                        />
                      </label>
                    </div>

                    <div className={styles.quickEditActions}>
                      <button type="button" className={styles.quickGhostBtn} onClick={handleQuickCancel}>
                        Cancel
                      </button>
                      <button
                        type="button"
                        className={styles.quickSaveBtn}
                        onClick={handleQuickSave}
                                              >
                        <Save size={13} />
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {activeSection === 'support' ? (
                <div className={styles.section}>
                  <div className={styles.quickEditCard}>
                    <div className={styles.quickEditHeader}>
                      <p className={styles.quickEditTitle}>Help &amp; Support</p>
                      <p className={styles.quickEditDescription}>
                        Tell us what you need — the team usually replies within 1 business day.
                      </p>
                    </div>

                    <div className={styles.quickEditGrid}>
                      <label className={styles.quickField}>
                        <span>Topic</span>
                        <select
                          value={supportForm.topic}
                          onChange={handleSupportFieldChange('topic')}
                        >
                          {SUPPORT_TOPICS.map((topic) => (
                            <option key={topic} value={topic}>
                              {topic}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className={styles.quickField}>
                        <span>Related loan (optional)</span>
                        <input
                          type="text"
                          value={supportForm.relatedLoan}
                          onChange={handleSupportFieldChange('relatedLoan')}
                          maxLength={64}
                        />
                      </label>
                      <label className={`${styles.quickField} ${styles.quickFieldFull}`}>
                        <span>Subject</span>
                        <input
                          type="text"
                          value={supportForm.subject}
                          onChange={handleSupportFieldChange('subject')}
                          placeholder="Brief summary of your request"
                          maxLength={160}
                        />
                      </label>
                      <label className={`${styles.quickField} ${styles.quickFieldFull}`}>
                        <span>Message</span>
                        <textarea
                          rows={5}
                          value={supportForm.message}
                          onChange={handleSupportFieldChange('message')}
                          placeholder="Describe the issue or question. Include steps, a loan number, or a screenshot reference if it helps."
                        />
                      </label>
                    </div>

                    <div className={styles.quickEditActions}>
                      <span className={styles.supportAlt}>
                        Prefer email? <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                      </span>
                      {supportFileName ? (
                        <span className={styles.supportFileName}>{supportFileName}</span>
                      ) : null}
                      <span className={styles.quickEditSpacer} />
                      <button
                        type="button"
                        className={styles.quickGhostBtn}
                        onClick={() => supportFileInputRef.current?.click()}
                      >
                        <Paperclip size={13} />
                        Attach file
                      </button>
                      <button
                        type="button"
                        className={styles.quickSaveBtn}
                        onClick={handleSupportSend}
                        disabled={
                          isSendingSupportTicket ||
                          !supportForm.subject.trim() ||
                          !supportForm.message.trim()
                        }
                      >
                        <SendHorizontal size={13} />
                        {isSendingSupportTicket ? 'Sending...' : 'Send message'}
                      </button>
                      <input
                        ref={supportFileInputRef}
                        type="file"
                        onChange={handleSupportFileChange}
                        style={{ display: 'none' }}
                      />
                    </div>
                  </div>
                </div>
              ) : null}

              {activeSection === 'work' ? (
                <div className={styles.section}>
                  <div className={styles.mainSectionCard}>
                    <p className={styles.workAreaTitle}>Choose where you want to work next</p>
                    <p className={styles.workAreaDescription}>
                      Open the part of the platform you use most often for operations,
                      reporting, and borrower management.
                    </p>

                    {WORK_AREA_GROUPS.map(({ key, title, description }) => {
                      const groupItems = key === 'primary' ? b2bPrimaryItems : b2bAdminItems;
                      if (!groupItems.length) return null;
                      return (
                        <div key={key} className={styles.workAreaSection}>
                          <p className={styles.workAreaSectionTitle}>{title}</p>
                          <p className={styles.workAreaSectionDescription}>{description}</p>
                          <div className={styles.workAreaGrid}>
                            {groupItems.map((item) => {
                              const Icon = item.icon;
                              return (
                                <button
                                  type="button"
                                  key={item.key}
                                  className={styles.workAreaCard}
                                  onClick={item.onClick}
                                >
                                  <span className={styles.actionLeft}>
                                    <span className={styles.actionGridIconWrap}>
                                      <Icon className={styles.actionGridIcon} />
                                    </span>
                                    <span className={styles.actionDetailBody}>
                                      <span className={styles.actionGridTitle}>{item.label}</span>
                                      <span className={styles.actionGridDescription}>{item.description}</span>
                                    </span>
                                  </span>
                                  <ChevronRight className={styles.actionArrow} />
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {activeSection === 'roles' && canAccessSection(SECTIONS.ADMIN, normalizedRole) ? (
                <div className={styles.section}>
                  <div className={styles.mainSectionCard}>
                    <RolesAccessPanel />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </>
    </div>
  );
};

export default AccountQuickPanel;
